/* eslint-disable eslint-comments/disable-enable-pair -- need custom buttons */
/* eslint-disable @typescript-eslint/no-deprecated -- need custom buttons */
import { Button } from "@tql/ui-components-react/kendo/buttons";
import {
	ChartArea,
	ChartCategoryAxis,
	ChartCategoryAxisItem,
	ChartLegend,
	ChartSeries,
	ChartSeriesItem,
	ChartValueAxis,
	ChartValueAxisItem,
} from "@tql/ui-components-react/kendo/charts";
import type { SVGIcon } from "@tql/ui-components-react/kendo/common";
import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Skeleton } from "@tql/ui-components-react/kendo/indicators";
import { Chart } from "@tql/ui-components-react/tql/charts";
import {
	alertCircle,
	checkCircleBroken,
	fileShield_02,
	markerPin_01,
	package_,
	truck_01,
} from "@tql/ui-components-react/untitled/icons";
import { useNavigate } from "react-router";

import type { ShipmentManagement } from "~/features/ar-dashboard/schemas/dashboard-schemas.ts";
import { formatNumber } from "~/shared/utils/string-fns.ts";

import { ArDashboardCard } from "../ar-dashboard-card/ar-dashboard-card.tsx";
import { IndicatorCard } from "../indicator-card/indicator-card.tsx";
import styles from "./shipment-management-card.module.css";

export type LoadsNavigationFilters = {
	customers: { id: number; name: string }[];
	ltlBrokerIds: number[];
	primaryBrokerIds: number[];
};

type ShipmentManagementBarStatusRowProps = {
	icon: SVGIcon | undefined;
	label: string;
	count1: number;
	count2: number;
	onGoodStandingClick?: () => void;
	onOutstandingIssuesClick?: () => void;
};

const ShipmentManagementBarStatusRow = ({
	icon,
	label,
	count1,
	count2,
	onGoodStandingClick,
	onOutstandingIssuesClick,
}: ShipmentManagementBarStatusRowProps) => {
	// When both counts are 0 there is no data to stack, so the bar collapses
	// and renders nothing. Fall back to a full empty (base-subtle) bar so the
	// progress bar still shows.
	const isEmpty = count1 + count2 === 0;
	const [firstSeries, secondSeries]: number[][] = isEmpty
		? [[0], [1]]
		: [[count1], [count2]];
	return (
		<div className={styles.shipmentManagementStatusBarRow}>
			<div className={styles.shipmentManagementStatusBarRowContent}>
				<div className={styles.shipmentManagementStatusBarRowTitle}>
					<SvgIcon icon={icon} size={"large"} />
					<span className={styles.labelSpan}>{label}</span>
					<span>{formatNumber(count1 + count2)}</span>
				</div>

				<div className={styles.shipmentManagementStatusBarChart}>
					<Chart>
						<ChartLegend visible={false} />
						<ChartArea
							background={"var(--kendo-color-app-surface)"}
							margin={0}
						/>
						<ChartCategoryAxis>
							<ChartCategoryAxisItem
								labels={{ visible: false }}
								line={{ visible: false }}
								majorGridLines={{ visible: false }}
								majorTicks={{ visible: false }}
								minorGridLines={{ visible: false }}
								minorTicks={{ visible: false }}
							/>
						</ChartCategoryAxis>
						<ChartValueAxis>
							<ChartValueAxisItem
								labels={{ visible: false }}
								line={{ visible: false }}
								majorGridLines={{ visible: false }}
								majorTicks={{ visible: false }}
								minorGridLines={{ visible: false }}
								minorTicks={{ visible: false }}
							/>
						</ChartValueAxis>
						<ChartSeries>
							<ChartSeriesItem
								color={"var(--kendo-color-primary-legacy)"}
								data={firstSeries}
								gap={0}
								name={"Series 1"}
								spacing={0}
								stack={{ type: "100%" }}
								tooltip={{ visible: !isEmpty }}
								type={"bar"}
							/>
							<ChartSeriesItem
								color={"var(--kendo-color-base-subtle)"}
								data={secondSeries}
								gap={0}
								name={"Series 2"}
								spacing={0}
								tooltip={{ visible: !isEmpty }}
								type={"bar"}
							/>
						</ChartSeries>
					</Chart>
				</div>
			</div>
			<div className={styles.alertButtons}>
				<div>
					<Button
						fillMode={"outline"}
						onClick={onGoodStandingClick}
						rounded={"small"}
						svgIcon={checkCircleBroken}
						themeColor={"primary"}
					>
						{formatNumber(count1)}
					</Button>
				</div>
				<div>
					<Button
						fillMode={"outline"}
						onClick={onOutstandingIssuesClick}
						rounded={"small"}
						svgIcon={alertCircle}
						themeColor={"error"}
					>
						{formatNumber(count2)}
					</Button>
				</div>
			</div>
		</div>
	);
};

type ShipmentManagementCardContentProps = {
	data: ShipmentManagement;
	isLoading: boolean;
	isFetching: boolean;
	loadsNavigationFilters?: LoadsNavigationFilters;
};

const ShipmentManagementBarStatusRowSkeleton = () => {
	return (
		<div className={styles.shipmentManagementStatusBarRow}>
			<div className={styles.shipmentManagementStatusBarRowContent}>
				<div className={styles.shipmentManagementStatusBarRowTitle}>
					<Skeleton shape={"circle"} style={{ width: 24, height: 24 }} />
					<Skeleton shape={"text"} style={{ flex: 1 }} />
					<Skeleton shape={"text"} style={{ width: 24 }} />
				</div>
				<Skeleton
					shape={"rectangle"}
					style={{
						height: 12,
						borderRadius: 4,
						marginLeft: "calc(var(--kendo-spacing-4) + 15px)",
					}}
				/>
			</div>
			<div className={styles.alertButtons}>
				<Skeleton
					shape={"rectangle"}
					style={{ width: 50, height: 30, borderRadius: 4 }}
				/>
				<Skeleton
					shape={"rectangle"}
					style={{ width: 50, height: 30, borderRadius: 4 }}
				/>
			</div>
		</div>
	);
};

const ShipmentManagementCardContent = ({
	data: shipmentData,
	isLoading,
	isFetching,
	loadsNavigationFilters,
}: ShipmentManagementCardContentProps) => {
	const navigate = useNavigate();
	const navigateToTrack = (filter: string) => {
		const urlParams = new URLSearchParams();
		urlParams.set("filter", filter);
		if (loadsNavigationFilters) {
			for (const id of loadsNavigationFilters.ltlBrokerIds) {
				urlParams.append("ltlBrokerIds", id.toString());
			}
			for (const id of loadsNavigationFilters.primaryBrokerIds) {
				urlParams.append("primaryBrokerIds", id.toString());
			}
			// The Loads page reads `selectedCustomers` from sessionStorage on
			// mount; piggy-back on that mechanism so we don't need a second
			// transport for customer name display.
			if (loadsNavigationFilters.customers.length > 0) {
				globalThis.sessionStorage.setItem(
					"selectedCustomers",
					JSON.stringify(loadsNavigationFilters.customers),
				);
			}
		}
		navigate(`/track?${urlParams.toString()}`);
	};
	const showSkeleton = isLoading || isFetching;
	const { exceptions, statuses, shipmentProgress } = shipmentData;

	const totalGoodStanding =
		shipmentProgress.todaysPickups.inGoodStanding +
		shipmentProgress.inTransit.inGoodStanding +
		shipmentProgress.todaysDeliveries.inGoodStanding;
	const totalOutstandingIssues =
		shipmentProgress.todaysPickups.outstandingIssues +
		shipmentProgress.inTransit.outstandingIssues +
		shipmentProgress.todaysDeliveries.outstandingIssues;
	const totalShipments = totalGoodStanding + totalOutstandingIssues;

	const data = [
		{
			type: "In Good Standing",
			count: totalGoodStanding,
			color: "var(--kendo-color-primary-legacy)",
		},
		{
			type: "Outstanding Issues",
			count: totalOutstandingIssues,
			color: "var(--kendo-color-error)",
		},
	];

	const donutCenterRenderer = () => (
		<span>
			<h4>{showSkeleton ? "..." : formatNumber(totalShipments)}</h4>
		</span>
	);

	return (
		<div className={styles.shipmentManagementCardContent}>
			<div className={styles.shipmentManagementRow1}>
				{showSkeleton ? (
					<Skeleton shape={"circle"} style={{ width: 75, height: 75 }} />
				) : (
					<Chart
						className={styles.shipmentManagementDonut}
						donutCenterRender={donutCenterRenderer}
						style={{ width: 75, height: 75 }}
					>
						<ChartLegend visible={false} />
						<ChartArea
							background={"var(--kendo-color-app-surface)"}
							margin={0}
						/>
						<ChartSeries>
							<ChartSeriesItem
								categoryField={"type"}
								colorField={"color"}
								data={data}
								field={"count"}
								holeSize={25}
								name={"Shipment Management"}
								size={10}
								tooltip={{ visible: true }}
								type={"donut"}
							/>
						</ChartSeries>
					</Chart>
				)}
				<IndicatorCard
					count={exceptions.immediateActionCount}
					isLoading={showSkeleton}
					label={"Immediate Action"}
					onClick={() => {
						navigateToTrack("isImmediateAction");
					}}
					type={"error"}
				/>
				<IndicatorCard
					count={exceptions.needsAcknowledgedCount}
					isLoading={showSkeleton}
					label={"Exceptions to Acknowledge"}
					onClick={() => {
						navigateToTrack("isNeedsAcknowledged");
					}}
					type={"info"}
				/>
			</div>
			<div className={styles.shipmentManagementRow2}>
				<IndicatorCard
					border={true}
					count={statuses.notDispatchedDatedToday}
					isLoading={showSkeleton}
					label={"Not Dispatched - Dated Today"}
					onClick={() => {
						navigateToTrack("isNotDispatched");
					}}
					type={
						statuses.notDispatchedDatedToday === 0 ? "base" : "error-strong"
					}
				/>
				<IndicatorCard
					border={true}
					count={statuses.pastPickupsNotYetTracking}
					isLoading={showSkeleton}
					label={"Past Pickups - Not Yet Tracking"}
					onClick={() => {
						navigateToTrack("isPastPickups");
					}}
					type={
						statuses.pastPickupsNotYetTracking === 0 ? "base" : "error-strong"
					}
				/>
			</div>
			{showSkeleton ? (
				<>
					<ShipmentManagementBarStatusRowSkeleton />
					<ShipmentManagementBarStatusRowSkeleton />
					<ShipmentManagementBarStatusRowSkeleton />
				</>
			) : (
				<>
					<ShipmentManagementBarStatusRow
						count1={shipmentProgress.todaysPickups.inGoodStanding}
						count2={shipmentProgress.todaysPickups.outstandingIssues}
						icon={markerPin_01}
						label={"Today's Pickups"}
						onGoodStandingClick={() => {
							navigateToTrack("isPickupsGoodStanding");
						}}
						onOutstandingIssuesClick={() => {
							navigateToTrack("isPickupsOutstanding");
						}}
					/>
					<ShipmentManagementBarStatusRow
						count1={shipmentProgress.inTransit.inGoodStanding}
						count2={shipmentProgress.inTransit.outstandingIssues}
						icon={truck_01}
						label={"In Transit"}
						onGoodStandingClick={() => {
							navigateToTrack("isTransitGoodStanding");
						}}
						onOutstandingIssuesClick={() => {
							navigateToTrack("isTransitOutstanding");
						}}
					/>
					<ShipmentManagementBarStatusRow
						count1={shipmentProgress.todaysDeliveries.inGoodStanding}
						count2={shipmentProgress.todaysDeliveries.outstandingIssues}
						icon={package_}
						label={"Today's Deliveries"}
						onGoodStandingClick={() => {
							navigateToTrack("isDeliveriesGoodStanding");
						}}
						onOutstandingIssuesClick={() => {
							navigateToTrack("isDeliveriesOutstanding");
						}}
					/>
				</>
			)}
		</div>
	);
};

export const ShipmentManagementCard = ({
	data,
	isLoading,
	isFetching,
	loadsNavigationFilters,
}: ShipmentManagementCardContentProps) => {
	return (
		<ArDashboardCard
			content={
				<ShipmentManagementCardContent
					data={data}
					isFetching={isFetching}
					isLoading={isLoading}
					loadsNavigationFilters={loadsNavigationFilters}
				/>
			}
			icon={fileShield_02}
			title={"Shipment Management"}
		/>
	);
};
